import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { getProceedScreening } from "../../api/agent";
import { useMutation } from "@tanstack/react-query";
import { usePlaygroundContext } from "../Playground";
import MessagePopup from "../../layout/messagePopup";

interface Certification {
  name: string;
  requirement: string;
}

interface Education {
  degree: string;
  requirement: string;
}

interface FunctionalSkill {
  skill: string;
  level: string;
  requirement: string;
  keywords: string[];
}

interface TechnicalSkill {
  skill: string;
  level: string;
  requirement: string;
  keywords: string[];
}

interface Language {
  name: string;
  level: string;
}

interface Industry {
  name: string;
  keywords: string[];
}

interface Domain {
  name: string;
  keywords: string[];
}

interface JobDetails {
  aboutClient: string;
  certifications: Certification[];
  description: string;
  domain: Domain;
  education: Education[];
  functionalSkills: FunctionalSkill[];
  industry: Industry;
  jobAssignmentType: string;
  jobTitle: string;
  jobType: string;
  job_shift_end: string;
  job_shift_start: string;
  languages: Language[];
  licenses: string[];
  location: string;
  rolesAndResponsibilities: string[];
  technicalSkills: TechnicalSkill[];
  yearsOfExperience: number | null;
}
interface JobScreeningprops {
  setShowjobScreeing: any;
}

const JobScreening: React.FC<JobScreeningprops> = ({ setShowjobScreeing }) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const companyName = playground.companyName;
  const generatedResponse = playground.jobDescription;
  const selectedCandidateDetails = playground.selectedCandidate;
  useEffect(() => {
    setJobDetails(generatedResponse.messageList[0]);
  }, []);
  const [isLoading, setIsLoading] = React.useState(false);
  const [popupOpen, setPopupOpen] = React.useState(false);

  const { mutate } = useMutation({
    mutationFn: (payload: any) => getProceedScreening(payload),
    onSuccess: (data: any) => {
      setPlayground((prev) => ({ ...prev, screeningQuestions: data, page: 7 }));
      setPopupOpen(false);
    },
    onError: (error: any) => {
      console.error("Error sending payload:", error);
      setPopupOpen(false);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  // Trigger scrollToTop when component is loaded
  useEffect(() => {
    scrollToTop();
  }, []);

  const handleNavigate = () => {
    setIsLoading(true);
    setPopupOpen(true);
    // Create payload from selectedCandidateDetails
    const payload = {
      company_name: companyName,
      enhancegeneratejd: selectedCandidateDetails.generateJd,
      candidate_data: {
        eximScore: selectedCandidateDetails.eximScore,
        search_results: {
          annualRate: selectedCandidateDetails?.annualRate || 0,
          difference_radius:
            selectedCandidateDetails?.difference_radius || null,
          email: selectedCandidateDetails.email,
          eximiusId: selectedCandidateDetails.eximiusId,
          firstName: selectedCandidateDetails.firstName,
          hotListed: selectedCandidateDetails.hotListed || "n",
          hourlyRate: selectedCandidateDetails.hourlyRate || 0,
          id: selectedCandidateDetails.eximiusId,
          jobTitle: selectedCandidateDetails.jobTitle,
          lastName: selectedCandidateDetails.lastName,
          lastUpdated: selectedCandidateDetails.lastUpdated,
          location: {
            city: selectedCandidateDetails.location?.city || null,
            country: selectedCandidateDetails.location?.country || null,
            countryCode: selectedCandidateDetails.location?.countryCode || null,
            latitude: selectedCandidateDetails.location?.latitude || null,
            longitude: selectedCandidateDetails.location?.longitude || null,
            postalCode: selectedCandidateDetails.location?.postalCode || null,
            state: selectedCandidateDetails.location?.state || null,
            street: selectedCandidateDetails.location?.street || null,
          },
          phone: selectedCandidateDetails.phone || [],
          scorecard: selectedCandidateDetails.scorecard,
          source: selectedCandidateDetails.source,
          status: selectedCandidateDetails.status,
          willingToRelocate:
            selectedCandidateDetails.willingToRelocate || false,
          yearsOfExperience: selectedCandidateDetails.yearsOfExperience || 0,
        },
      },
    };

    // Call the API with the payload
    mutate(payload);
  };

  const [jobDetails, setJobDetails] = React.useState<JobDetails | null>(null);

  const handlePrevious = () => {
    setShowjobScreeing(false);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  return (
    <Box p={isMobile ? 0 : 4} pt={0}>
      <Grid container spacing={2}>
        <Box>
          {/* Product Simulation Guide */}
          <Card
            sx={{
              flex: 1,
              minWidth: 300,
              backgroundColor: "transparent",
              color: "white",
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Card
                sx={{
                  flex: 1,
                  minWidth: 300,
                  // height: "150px",
                  // marginTop: "10px",
                  // padding: 2,
                  // paddingTop:0,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "600",
                      fontSize: "24px",
                      mb: 2,
                      background: "linear-gradient(90deg, #538DFF, #A365F3)",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    AI-powered Candidate Engagement
                  </Typography>
                  <Typography variant="body1" color="white">
                    Eximius AI sends personalized messages to selected
                    candidates, showcasing how their skills align with your
                    needs. Our smart automation manages communication, interview
                    scheduling, and responses, saving you hours of work.
                  </Typography>
                  <Typography mt={1} color="white">
                    Shown below is an example of how a candidate receives a
                    personalized screening invitation via email. To proceed
                    further, respond by clicking 'Proceed to Screening' through
                    a secure, mobile-friendly interface.
                  </Typography>
                </CardContent>
              </Card>
            </CardContent>
          </Card>

          {/* Job Opportunity Section */}
          <Card
            sx={{
              flex: 1,
              minWidth: 300,
              backgroundColor: "transparent",
              color: "white",
              marginTop: "-10px",
              boxShadow: "none",
              paddingLeft: 2,
              paddingtop: 0,
            }}
          >
            <CardContent>
              <Card
                sx={{
                  flex: 1,
                  minWidth: 300,
                  // padding: 2,
                  backgroundColor: "#050d2e",
                  boxShadow: "none",
                  border: "1px solid rgba(66, 73, 101, 1)",
                  height: "400px",
                  overflow: "hidden",
                }}
              >
                <CardContent
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  <Typography variant="body1" color="white" fontWeight={"bold"}>
                    Hi {selectedCandidateDetails?.firstName}{" "}
                    {selectedCandidateDetails?.lastName},
                  </Typography>
                  <Typography mt={1} color="white">
                    Hope you are doing well!
                  </Typography>

                  <Typography variant="body2" mt={1} color="white">
                    I am an AI-powered recruitment assistant with {companyName}.
                    I came across your impressive profile and believe you could
                    be an excellent fit for an exciting role we currently have
                    open.
                  </Typography>
                  <List dense>
                    <ListItem>
                      <Typography
                        variant="body2"
                        color="white"
                        sx={{ whiteSpace: "nowrap", mr: 1 }}
                      >
                        <span style={{ color: "#538DFF", fontWeight: "bold" }}>
                          <b>• Job Title:</b>
                        </span>{" "}
                        <Typography
                          variant="body2"
                          color="white"
                          component="span"
                          sx={{ ml: "5px" }}
                        >
                          {jobDetails?.jobTitle}
                        </Typography>
                      </Typography>
                    </ListItem>
                    <ListItem
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    >
                      <Typography
                        variant="body2"
                        color="white"
                        sx={{ whiteSpace: "nowrap", mr: 1 }}
                      >
                        <span style={{ color: "#538DFF", fontWeight: "bold" }}>
                          <b>• Job Description:</b>
                        </span>
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          textAlign: "left",
                          flex: 1,
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                        }}
                      >
                        <Typography variant="body2" color="white">
                          {jobDetails?.description}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>

                  <Typography variant="body2" color="white">
                    Use the options below to apply or let us know if you're not
                    currently looking for new opportunities.
                  </Typography>

                  <Typography variant="body2" mt={1} color="white">
                    Please Note, these options will be available for the next 24
                    hours only.
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    mt={1}
                    color="#538DFF"
                  >
                    Why Consider Eximius?
                  </Typography>
                  <List dense sx={{ marginLeft: "-18px" }}>
                    <ListItem>
                      <Typography variant="body2" color="white">
                        <span
                          style={{
                            color: "#538DFF",
                            fontWeight: "bold",
                            marginRight: "5px",
                          }}
                        >
                          Advanced Job Matching:
                        </span>{" "}
                        AI connects you with jobs that perfectly match your
                        skills.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2" color="white">
                        <span
                          style={{
                            color: "#538DFF",
                            fontWeight: "bold",
                            marginRight: "5px",
                          }}
                        >
                          Diverse Opportunities:
                        </span>{" "}
                        Explore thousands of job openings across various
                        sectors.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2" color="white">
                        <span
                          style={{
                            color: "#538DFF",
                            fontWeight: "bold",
                            marginRight: "5px",
                          }}
                        >
                          Pre-Screening:
                        </span>{" "}
                        Employers benefit from a vast pipeline of pre-screened
                        talent to meet their business needs.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2" color="white">
                        <span
                          style={{
                            color: "#538DFF",
                            fontWeight: "bold",
                            marginRight: "5px",
                          }}
                        >
                          Referral Program:
                        </span>{" "}
                        Earn extra by referring other talented candidates.
                      </Typography>
                    </ListItem>
                  </List>

                  <Typography variant="body2" mt={2} color="white">
                    We look forward to potentially working with you and taking
                    the next steps in your career journey together.
                  </Typography>

                  <Typography
                    variant="body1"
                    mt={2}
                    fontWeight="bold"
                    color="white"
                    sx={{ mb: 2 }}
                  >
                    Regards, <br />
                    Eximius Team
                  </Typography>
                </CardContent>
              </Card>
              <Box
                mt={2}
                display="flex"
                justifyContent="flex-start"
                gap={2}
                flexDirection={isMobile ? "column-reverse" : "row"} // Reverse order on mobile
              >
                <Button
                  variant="outlined"
                  type="button"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#7C7C7C",
                    color: "white",
                    height: "44px",
                    borderRadius: "45px",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handlePrevious}
                >
                  Previous
                </Button>

                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    height: "44px",
                    color: "#fff",
                    bgcolor: "#007BFF",
                    borderRadius: "45px",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    "&:hover": {
                      bgcolor: "#0052CC",
                    },
                    "&:disabled": {
                      bgcolor: "#0052CC",
                      color: "#fff",
                    },
                  }}
                  onClick={handleNavigate}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={20} />}
                >
                  Proceed to Screening
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Grid>
      <MessagePopup
        open={popupOpen}
        message="Our AI Engine is now creating a dynamic voice and chat bot for screening the candidate with pertinent questions about the job you created. Please wait..."
      />
    </Box>
  );
};

export default JobScreening;
